// Einheiten-Katalog: Kürzel und Bezeichnungen für die Anzeige, UN/ECE-Codes für die E-Rechnung.
// Der Katalog ist die Vorlage für die Grunddaten (Tabelle `unit`); Mandanten dürfen eigene Einheiten anlegen.
import { formatBreakdown } from './format'
import { breakdownQty, fromBaseQty, toBaseQty, type Converted, type Qty } from './qty'

export type UnitKind = 'piece' | 'package' | 'mass' | 'volume' | 'length' | 'area' | 'time'

export interface UnitDef {
  /** stabiler Schlüssel (englisch, klein) */
  code: string
  /** Einzahl: „Kasten“ */
  label: string
  /** Mehrzahl: „Kästen“ */
  plural: string
  /** Kurzform für Tabellen: „Ka“ */
  short: string
  kind: UnitKind
  /** übliche Nachkommastellen bei der Anzeige (0 = Stückware) */
  decimals: 0 | 1 | 2 | 3
  /** UN/ECE Rec. 20 bzw. Rec. 21 mit X-Präfix (EN 16931, BT-130) */
  unece: string
}

const def = (
  code: string,
  label: string,
  plural: string,
  short: string,
  kind: UnitKind,
  decimals: 0 | 1 | 2 | 3,
  unece: string,
): UnitDef => ({ code, label, plural, short, kind, decimals, unece })

export const UNIT_CATALOG = [
  def('piece', 'Stück', 'Stück', 'St', 'piece', 0, 'H87'),
  def('bottle', 'Flasche', 'Flaschen', 'Fl', 'package', 0, 'XBO'),
  def('can', 'Dose', 'Dosen', 'Do', 'package', 0, 'XCX'),
  def('crate', 'Kasten', 'Kästen', 'Ka', 'package', 0, 'XCR'),
  def('carton', 'Karton', 'Kartons', 'Kt', 'package', 0, 'XCT'),
  def('pack', 'Packung', 'Packungen', 'Pck', 'package', 0, 'XPK'),
  def('tray', 'Tray', 'Trays', 'Tr', 'package', 0, 'XPU'),
  def('keg', 'Fass', 'Fässer', 'Fa', 'package', 0, 'XKG'),
  def('pallet', 'Palette', 'Paletten', 'Pal', 'package', 0, 'XPX'),
  def('bag', 'Beutel', 'Beutel', 'Btl', 'package', 0, 'XBG'),
  def('sack', 'Sack', 'Säcke', 'Sa', 'package', 0, 'XSA'),
  def('set', 'Set', 'Sets', 'Set', 'piece', 0, 'SET'),
  def('pair', 'Paar', 'Paar', 'Pa', 'piece', 0, 'PR'),
  def('kg', 'Kilogramm', 'Kilogramm', 'kg', 'mass', 3, 'KGM'),
  def('g', 'Gramm', 'Gramm', 'g', 'mass', 0, 'GRM'),
  def('l', 'Liter', 'Liter', 'l', 'volume', 3, 'LTR'),
  def('ml', 'Milliliter', 'Milliliter', 'ml', 'volume', 0, 'MLT'),
  def('hl', 'Hektoliter', 'Hektoliter', 'hl', 'volume', 3, 'HLT'),
  def('m', 'Meter', 'Meter', 'm', 'length', 2, 'MTR'),
  def('m2', 'Quadratmeter', 'Quadratmeter', 'm²', 'area', 2, 'MTK'),
  def('hour', 'Stunde', 'Stunden', 'Std', 'time', 2, 'HUR'),
  def('day', 'Tag', 'Tage', 'Tg', 'time', 0, 'DAY'),
] as const satisfies readonly UnitDef[]

export type UnitCode = (typeof UNIT_CATALOG)[number]['code']

const byCode: ReadonlyMap<string, UnitDef> = new Map(UNIT_CATALOG.map((u) => [u.code, u]))

export const findUnit = (code: string): UnitDef | undefined => byCode.get(code)

/** Kurzform für Tabellen; unbekannte Codes werden unverändert gezeigt (mandanteneigene Einheiten). */
export const unitShort = (code: string): string => byCode.get(code)?.short ?? code

/** „1 Kasten“, „2 Kästen“ – `count` in ganzen Einheiten. */
export function unitLabel(code: string, count = 1): string {
  const unit = byCode.get(code)
  if (!unit) return code
  return Math.abs(count) === 1 ? unit.label : unit.plural
}

/** Mengen dieser Einheit sind üblicherweise ganzzahlig (Stück, Gebinde). */
export const isCountUnit = (code: string): boolean => (byCode.get(code)?.decimals ?? 0) === 0

// ---- Gebinde eines Artikels (item_unit): Flasche ↔ Kasten ↔ Palette ---------------------------------

/** Ein Gebinde eines Artikels – so viel, wie Umrechnung und Anzeige brauchen (Zeile aus `item_unit`). */
export interface ItemUnitRef {
  /** item_unit.id */
  id: string
  /** Einheitencode ('bottle', 'crate') – aus dem Katalog oder mandanteneigen */
  code: string
  /** Basismenge je Einheit in Tausendstel: Flasche 1000, Kasten 20_000, Palette 800_000 */
  baseQty: Qty
  /** Kurzform einer mandanteneigenen Einheit; sonst gilt der Katalog */
  short?: string | null
}

export interface ItemUnitSplit {
  /** ganze Gebinde, größtes zuerst; Gebinde mit 0 entfallen */
  parts: { unitId: string; code: string; short: string; count: number }[]
  /** Rest in Basiseinheit (Tausendstel), der in kein Gebinde ganzzahlig passt */
  rest: Qty
  negative: boolean
  /** „3 Ka + 4 Fl“ */
  text: string
}

function unitOf(units: readonly ItemUnitRef[], unitId: string): ItemUnitRef {
  const unit = units.find((u) => u.id === unitId)
  if (!unit) throw new RangeError(`Dieses Gebinde gehört nicht zum Artikel: ${unitId}`)
  return unit
}

const shortOf = (unit: ItemUnitRef): string => unit.short ?? unitShort(unit.code)

/** Menge in einem Gebinde → Basismenge: 3 Ka (3000) bei Kasten 20 → 60 Fl (60_000). */
export function itemUnitToBase(units: readonly ItemUnitRef[], unitId: string, qty: Qty): Qty {
  return toBaseQty(qty, unitOf(units, unitId).baseQty)
}

/** Basismenge → Menge im Gebinde; `exact` ist false, wenn es nicht glatt aufgeht (64 Fl = 3,2 Ka). */
export function itemUnitFromBase(units: readonly ItemUnitRef[], unitId: string, baseQty: Qty): Converted {
  return fromBaseQty(baseQty, unitOf(units, unitId).baseQty)
}

/**
 * Beste Anzeige einer Basismenge in den Gebinden des Artikels: 64 Fl mit Kasten 20 → „3 Ka + 4 Fl“.
 * `only` beschränkt auf bestimmte Gebinde (z. B. ohne Palette); die Basiseinheit nimmt den Rest auf.
 */
export function itemUnitSplit(
  baseQty: Qty,
  units: readonly ItemUnitRef[],
  options: { only?: readonly string[] } = {},
): ItemUnitSplit {
  const base = units.find((u) => u.baseQty === 1000)
  const usable = units.filter((u) => u === base || !options.only || options.only.includes(u.id))
  // gleiche Größe doppelt (zwei Kästen mit 20) → das erste Gebinde zählt
  const bySize = new Map<Qty, ItemUnitRef>()
  for (const unit of usable) if (!bySize.has(unit.baseQty)) bySize.set(unit.baseQty, unit)
  const byKey = new Map([...bySize.values()].map((unit) => [unit.id, unit]))
  const breakdown = breakdownQty(
    baseQty,
    [...byKey.values()].map((unit) => ({ code: unit.id, baseQty: unit.baseQty })),
  )
  const parts = breakdown.parts.map((part) => {
    const unit = byKey.get(part.code) as ItemUnitRef
    return { unitId: unit.id, code: unit.code, short: shortOf(unit), count: part.count }
  })
  const text = formatBreakdown(
    { parts: parts.map((part) => ({ code: part.short, count: part.count })), rest: breakdown.rest, negative: breakdown.negative },
    base ? shortOf(base) : '',
  )
  return { parts, rest: breakdown.rest, negative: breakdown.negative, text }
}
